/**
 * Memories route.
 *
 * Lists `users/{uid}/memories` (via `useMemories`) — facts the agent learned from
 * conversations, consolidated by the daily job, saved on request, or seeded in
 * the onboarding. Curadoria only: the user approves/rejects, edits, or deletes.
 * Criação é backend-only (as regras do Firestore negam `create` ao cliente), por
 * isso não há botão "Novo".
 *
 * Organização por status: "Pendentes de aprovação" no topo (só pendentes entram
 * no contexto do agente depois de aprovadas), "Aprovadas" agrupadas por
 * categoria, e "Rejeitadas" ao final. Filtro por categoria via DropdownMenu
 * (não há select/tabs/badge no kit).
 *
 * Kept mounted at path '/memories'. Exported as `MemoriesRoute`.
 */
import type { MemoryCategory, MemoryDoc } from '@agente/shared'
import { BookMarked, Check, ChevronDown, Loader, Pencil, RotateCcw, Trash2, X } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useClients } from '@/hooks/useClients'
import { useMemories } from '@/hooks/useMemories'
import { useAuth } from '@/lib/auth'
import { deleteMemory, updateMemoryContent, updateMemoryStatus } from '@/lib/firestore'
import { formatRelative } from '@/lib/format'

/** Rótulo pt-BR de cada categoria de memória. */
const CATEGORY_LABELS: Record<MemoryCategory, string> = {
  user_preference: 'Preferências',
  person: 'Pessoas',
  work_context: 'Contexto de trabalho',
}

/** Ordem estável das categorias na seção "Aprovadas" e no filtro. */
const CATEGORY_ORDER: MemoryCategory[] = ['user_preference', 'person', 'work_context']

/** Origem legível de uma memória, exibida como metadado discreto. */
const SOURCE_LABELS: Record<MemoryDoc['source'], string> = {
  daily_job: 'Extraída das conversas',
  chat_tool: 'Salva no chat',
  onboarding: 'Apresentação inicial',
}

type CategoryFilter = MemoryCategory | 'all'

const FILTER_OPTIONS: Array<{ value: CategoryFilter; label: string }> = [
  { value: 'all', label: 'Todas as categorias' },
  { value: 'user_preference', label: CATEGORY_LABELS.user_preference },
  { value: 'person', label: CATEGORY_LABELS.person },
  { value: 'work_context', label: CATEGORY_LABELS.work_context },
]

export function MemoriesRoute() {
  const { memories, loading, error } = useMemories()
  const { clients } = useClients()
  const [filter, setFilter] = useState<CategoryFilter>('all')
  const [editing, setEditing] = useState<MemoryDoc | null>(null)
  const [deleting, setDeleting] = useState<MemoryDoc | null>(null)

  // Nome do cliente vinculado (quando `clientId` casa com um perfil cadastrado),
  // para o texto discreto de vínculo nas memórias de pessoas.
  const clientNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of clients) map.set(c.id, c.name)
    return map
  }, [clients])

  const { pending, approved, rejected } = useMemo(() => {
    const visible = filter === 'all' ? memories : memories.filter((m) => m.category === filter)
    const pending: MemoryDoc[] = []
    const approved: MemoryDoc[] = []
    const rejected: MemoryDoc[] = []
    for (const m of visible) {
      if (m.status === 'pending') pending.push(m)
      else if (m.status === 'approved') approved.push(m)
      else if (m.status === 'rejected') rejected.push(m)
    }
    return { pending, approved, rejected }
  }, [memories, filter])

  // Aprovadas agrupadas por categoria, mantendo a ordem estável das categorias.
  const approvedByCategory = useMemo(() => {
    return CATEGORY_ORDER.map((category) => ({
      category,
      items: approved.filter((m) => m.category === category),
    })).filter((group) => group.items.length > 0)
  }, [approved])

  const hasAny = memories.length > 0

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 py-6 md:p-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Memórias</h1>
        <p className="text-muted-foreground text-sm">
          Fatos que o agente aprende nas conversas, consolidados uma vez por dia. As pendentes só
          entram no contexto do agente depois que você aprova.
        </p>
      </header>

      {error ? (
        <p className="text-destructive text-sm">Não foi possível carregar as memórias: {error}</p>
      ) : loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : !hasAny ? (
        <EmptyState />
      ) : (
        <>
          <div className="flex items-center justify-end">
            <CategoryFilterPicker value={filter} onSelect={setFilter} />
          </div>

          <div className="flex flex-col gap-8">
            <PendingSection memories={pending} onEdit={setEditing} onDelete={setDeleting} />

            <ApprovedSection
              groups={approvedByCategory}
              clientNameById={clientNameById}
              onEdit={setEditing}
              onDelete={setDeleting}
            />

            <RejectedSection memories={rejected} onDelete={setDeleting} />

            {pending.length === 0 && approvedByCategory.length === 0 && rejected.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nenhuma memória nesta categoria.</p>
            ) : null}
          </div>
        </>
      )}

      {/* Edit dialog (keyed by id so fields reset between memories) */}
      <MemoryEditDialog
        key={editing?.id ?? 'edit-closed'}
        memory={editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
      />
      {/* Delete confirmation */}
      <DeleteMemoryDialog
        memory={deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
      />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
      <BookMarked className="text-muted-foreground size-8" aria-hidden="true" />
      <p className="text-muted-foreground mx-auto max-w-md text-sm">
        Nenhuma memória ainda. Elas são extraídas automaticamente das suas conversas todo dia, ou
        peça ao agente: "lembre disso".
      </p>
    </div>
  )
}

/** Dropdown de filtro por categoria (mesmo padrão do SettingPicker). */
function CategoryFilterPicker({
  value,
  onSelect,
}: {
  value: CategoryFilter
  onSelect: (value: CategoryFilter) => void
}) {
  const activeLabel = FILTER_OPTIONS.find((o) => o.value === value)?.label ?? 'Todas as categorias'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Filtrar por categoria"
          className="justify-between"
        >
          <span className="truncate">{activeLabel}</span>
          <ChevronDown className="size-4 shrink-0 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        {FILTER_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => onSelect(option.value)}
            className="justify-between"
          >
            {option.label}
            {option.value === value ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SectionHeading({ title, count }: { title: string; count: number }) {
  return (
    <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
      {title}
      <span className="ml-1.5 font-normal">({count})</span>
    </h2>
  )
}

function PendingSection({
  memories,
  onEdit,
  onDelete,
}: {
  memories: MemoryDoc[]
  onEdit: (memory: MemoryDoc) => void
  onDelete: (memory: MemoryDoc) => void
}) {
  if (memories.length === 0) return null
  return (
    <section className="flex flex-col gap-2" aria-label="Pendentes de aprovação">
      <SectionHeading title="Pendentes de aprovação" count={memories.length} />
      <p className="text-muted-foreground text-xs">
        Aprove para que o agente passe a considerar estes fatos.
      </p>
      <ul className="flex flex-col gap-2">
        {memories.map((m) => (
          <MemoryRow key={m.id} memory={m} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </ul>
    </section>
  )
}

function ApprovedSection({
  groups,
  clientNameById,
  onEdit,
  onDelete,
}: {
  groups: Array<{ category: MemoryCategory; items: MemoryDoc[] }>
  clientNameById: Map<string, string>
  onEdit: (memory: MemoryDoc) => void
  onDelete: (memory: MemoryDoc) => void
}) {
  if (groups.length === 0) return null
  const total = groups.reduce((sum, g) => sum + g.items.length, 0)
  return (
    <section className="flex flex-col gap-4" aria-label="Aprovadas">
      <SectionHeading title="Aprovadas" count={total} />
      {groups.map((group) => (
        <div key={group.category} className="flex flex-col gap-2">
          <h3 className="text-foreground/80 text-sm font-medium">
            {CATEGORY_LABELS[group.category]}
          </h3>
          <ul className="flex flex-col gap-2">
            {group.items.map((m) => (
              <MemoryRow
                key={m.id}
                memory={m}
                clientName={m.clientId ? clientNameById.get(m.clientId) : undefined}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

function RejectedSection({
  memories,
  onDelete,
}: {
  memories: MemoryDoc[]
  onDelete: (memory: MemoryDoc) => void
}) {
  if (memories.length === 0) return null
  return (
    <section className="flex flex-col gap-2 opacity-80" aria-label="Rejeitadas">
      <SectionHeading title="Rejeitadas" count={memories.length} />
      <ul className="flex flex-col gap-2">
        {memories.map((m) => (
          <MemoryRow key={m.id} memory={m} onDelete={onDelete} />
        ))}
      </ul>
    </section>
  )
}

/**
 * Uma memória. As ações mudam conforme o status:
 *  - pending: Aprovar / Rejeitar (+ editar / excluir)
 *  - approved: editar / excluir
 *  - rejected: Reaprovar / excluir
 */
function MemoryRow({
  memory,
  clientName,
  onEdit,
  onDelete,
}: {
  memory: MemoryDoc
  clientName?: string
  onEdit?: (memory: MemoryDoc) => void
  onDelete: (memory: MemoryDoc) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [pending, setPending] = useState(false)

  async function setStatus(status: MemoryDoc['status']) {
    if (!uid) return
    setPending(true)
    try {
      await updateMemoryStatus(uid, memory.id, status)
      toast.success(
        status === 'approved'
          ? 'Memória aprovada'
          : status === 'rejected'
            ? 'Memória rejeitada'
            : 'Memória atualizada',
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível atualizar a memória', { description: message })
    } finally {
      setPending(false)
    }
  }

  return (
    <li className="border-border bg-card flex items-start gap-3 rounded-lg border p-3 shadow-sm">
      <div className="min-w-0 flex-1">
        {memory.category === 'person' && memory.subject ? (
          <p className="text-sm font-semibold">{memory.subject}</p>
        ) : null}
        <p className="text-sm">{memory.content}</p>
        {clientName ? (
          <p className="text-muted-foreground mt-1 text-xs">Vinculada ao cliente {clientName}</p>
        ) : null}
        <p className="text-muted-foreground mt-1 text-xs">
          {SOURCE_LABELS[memory.source]} · {formatRelative(memory.updatedAt)}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {memory.status === 'pending' ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Aprovar memória: ${memory.content}`}
              disabled={pending}
              onClick={() => void setStatus('approved')}
            >
              {pending ? <Loader className="size-4 animate-spin" /> : <Check className="size-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Rejeitar memória: ${memory.content}`}
              disabled={pending}
              onClick={() => void setStatus('rejected')}
            >
              <X className="size-4" />
            </Button>
          </>
        ) : memory.status === 'rejected' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Reaprovar memória: ${memory.content}`}
            disabled={pending}
            onClick={() => void setStatus('approved')}
          >
            {pending ? (
              <Loader className="size-4 animate-spin" />
            ) : (
              <RotateCcw className="size-4" />
            )}
          </Button>
        ) : null}

        {onEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Editar memória: ${memory.content}`}
            onClick={() => onEdit(memory)}
          >
            <Pencil className="size-4" />
          </Button>
        ) : null}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Excluir memória: ${memory.content}`}
          className="text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(memory)}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </li>
  )
}

function DeleteMemoryDialog({
  memory,
  onOpenChange,
}: {
  memory: MemoryDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    if (!memory || !uid) return
    setDeleting(true)
    try {
      await deleteMemory(uid, memory.id)
      toast.success('Memória excluída')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível excluir a memória', { description: message })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open={memory !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir memória</DialogTitle>
          <DialogDescription>
            {memory
              ? `Tem certeza de que deseja excluir esta memória? "${memory.content}" Esta ação é permanente.`
              : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader className="size-4 animate-spin" /> : null}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MemoryEditDialog({
  memory,
  onOpenChange,
}: {
  memory: MemoryDoc | null
  onOpenChange: (open: boolean) => void
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null
  const [content, setContent] = useState(memory?.content ?? '')
  const [subject, setSubject] = useState(memory?.subject ?? '')
  const [category, setCategory] = useState<MemoryCategory>(memory?.category ?? 'user_preference')
  const [saving, setSaving] = useState(false)

  const canSave =
    content.trim().length > 0 && (category !== 'person' || subject.trim().length > 0) && !saving

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSave || !uid || !memory) return
    // subject só é enviado quando preenchido — o Firestore não aceita `undefined`
    // em updateDoc, e omitir a chave preserva o valor anterior.
    const patch: Pick<MemoryDoc, 'content' | 'category' | 'subject'> = {
      content: content.trim(),
      category,
    }
    const trimmedSubject = subject.trim()
    if (trimmedSubject) patch.subject = trimmedSubject
    setSaving(true)
    try {
      await updateMemoryContent(uid, memory.id, patch)
      toast.success('Memória atualizada')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar a memória', { description: message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={memory !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Editar memória</DialogTitle>
            <DialogDescription>
              Ajuste o texto, o assunto ou a categoria deste fato aprendido pelo agente.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="memory-category">Categoria</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id="memory-category"
                  type="button"
                  variant="outline"
                  className="w-full justify-between"
                >
                  <span className="truncate">{CATEGORY_LABELS[category]}</span>
                  <ChevronDown className="size-4 shrink-0 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[--radix-dropdown-menu-trigger-width] min-w-52"
              >
                {CATEGORY_ORDER.map((value) => (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => setCategory(value)}
                    className="justify-between"
                  >
                    {CATEGORY_LABELS[value]}
                    {value === category ? <Check className="size-4" /> : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="memory-subject">
              {category === 'person' ? 'Assunto (pessoa)' : 'Assunto (opcional)'}
            </Label>
            <Input
              id="memory-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={category === 'person' ? 'Nome da pessoa' : 'Assunto do fato'}
              required={category === 'person'}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="memory-content">Fato</Label>
            <Textarea
              id="memory-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Um fato autocontido, em uma frase."
              rows={3}
              autoFocus
              required
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSave}>
              {saving ? <Loader className="size-4 animate-spin" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default MemoriesRoute
